import { Model, type ModelProps } from '@/infra/model'
import { once } from 'lodash-es'
import { observable } from 'mobx'
import { Id } from '@/infra/id'

export interface JobProgressionStep {
  id: Id
  status: JobProgressionStepStatus
  response: JobProgressionStepStatus | null
  dateCreated?: Date
  dateResponded?: Date
  createdByUserPublicId?: string
}

export type JobProgressionProps = ModelProps<JobProgression>

/**
 * Progression information for a job where progression is enabled.
 */
export class JobProgression extends Model<JobProgression> {
  id = Id.none
  jobId = Id.none

  @observable accessor steps: JobProgressionStep[] = []
  @observable accessor progressionFlow: JobProgressionFlow = 'None'

  /**
   * Empty instance.
   */
  static get none() {
    return lazyNone()
  }
}

const lazyNone = once(() => new JobProgression())

export type JobProgressionStepStatus =
  | 'Unspecified'
  | 'JobStarted'
  | 'OpenItems'
  | 'DraftInReview'
  | 'DraftRejected'
  | 'DraftApproved'
  | 'AuthorizationRequested'
  | 'AuthorizationSigned'
  | 'ReturnFiled'
  | 'NotStarted'
  | 'DeliverableCompleted'
  | 'PreparationCompleted'
  | 'ReviewCompleted'
  | 'ExtensionNotStarted'
  | 'ExtensionInProgress'
  | 'ExtensionConfirmed'
  | 'ExtensionAccepted'
  | 'ExtensionRejected'
  | 'ProgressionWaitingOnClient'
  | 'ReadyToFile'

export type JobProgressionFlow =
  | 'None'
  | 'Retail'
  | 'Outsourcing'
  | 'Essential'
  | 'Preparation'
  | 'Review'
  | 'PersonalExtension'
  | 'BusinessExtension'
  | 'OutsourcingExtension'

export type OverrideableJobProgressionStepStatus = Extract<
  JobProgressionStepStatus,
  'ReturnFiled' | 'JobStarted' | 'OpenItems'
>

export type ExtensionProgressionFlow = 'Personal' | 'Business' | 'Outsourcing'

/**
 * Estimated tax payment captured when an extension is accepted.
 */
export interface EstimatedTaxPayment {
  federalAmount: number
  amountByState: { state: string; amount: number }[]
  isCalculated: boolean
}

/**
 * A transition of a job progression to its next step.
 */
export type JobProgressionTransition =
  | {
      step: Extract<
        JobProgressionStepStatus,
        | 'JobStarted'
        | 'OpenItems'
        | 'DraftInReview'
        | 'DraftApproved'
        | 'DraftRejected'
        | 'PreparationCompleted'
        | 'ReviewCompleted'
        | 'AuthorizationRequested'
        | 'AuthorizationSigned'
        | 'ReturnFiled'
        | 'DeliverableCompleted'
        | 'ExtensionConfirmed'
        | 'ExtensionRejected'
      >
    }
  | { step: 'ExtensionInProgress'; flow: ExtensionProgressionFlow }
  | { step: 'ReadyToFile'; setAsAdmin: boolean }
  | {
      step: 'ExtensionAccepted'
      estimatedTaxPayment: EstimatedTaxPayment | null
    }
